const base64URLPattern = /^[A-Za-z0-9_-]+$/;

export type OwnedBytes = Uint8Array<ArrayBuffer>;

export function concatBytes(...parts: readonly Uint8Array<ArrayBufferLike>[]): OwnedBytes {
  const length = parts.reduce((total, part) => total + part.byteLength, 0);
  const result = new Uint8Array(length);
  let offset = 0;
  for (const part of parts) {
    result.set(part, offset);
    offset += part.byteLength;
  }
  return result;
}

export function copyBytes(value: ArrayBuffer | ArrayBufferView<ArrayBufferLike>): OwnedBytes {
  const source =
    value instanceof ArrayBuffer
      ? new Uint8Array(value)
      : new Uint8Array(value.buffer, value.byteOffset, value.byteLength);
  const result = new Uint8Array(source.byteLength);
  result.set(source);
  return result;
}

export function bytesEqual(left: Uint8Array<ArrayBufferLike>, right: Uint8Array<ArrayBufferLike>): boolean {
  if (left.byteLength !== right.byteLength) return false;
  let difference = 0;
  for (let index = 0; index < left.byteLength; index += 1) {
    difference |= (left[index] ?? 0) ^ (right[index] ?? 0);
  }
  return difference === 0;
}

export function encodeBase64URL(value: Uint8Array<ArrayBufferLike>): string {
  let binary = "";
  const chunkSize = 0x8000;
  for (let offset = 0; offset < value.byteLength; offset += chunkSize) {
    const chunk = value.subarray(offset, Math.min(offset + chunkSize, value.byteLength));
    binary += String.fromCharCode(...chunk);
  }
  return btoa(binary).replaceAll("+", "-").replaceAll("/", "_").replace(/=+$/u, "");
}

export function decodeBase64URL(value: string): OwnedBytes {
  if (!value || !base64URLPattern.test(value) || value.includes("="))
    throw new Error("Value must be unpadded base64url");
  const padding = "=".repeat((4 - (value.length % 4)) % 4);
  const binary = atob(value.replaceAll("-", "+").replaceAll("_", "/") + padding);
  const result = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) result[index] = binary.charCodeAt(index);
  return result;
}

export function randomBytes(length: number): OwnedBytes {
  if (!Number.isInteger(length) || length < 1) throw new Error("Random byte length must be a positive integer");
  return crypto.getRandomValues(new Uint8Array(length));
}

export function utf8(value: string): OwnedBytes {
  return new TextEncoder().encode(value);
}
